import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { DX } from '../../theme/v2';

/**
 * The V2 table.
 *
 * The shared `components/Table` stays exactly as it is — 17 legacy pages are drawn by it, and this
 * is a redesign that lives beside the old one rather than on top of it. So this is a second table,
 * on the tokens: 11/700 captions, a 40px row, a sticky header, one hairline, and hover that tells
 * you which row a click would take.
 *
 * The column shape is the one the pages already write — `{ key, title, render, renderHeadCell,
 * thStyle, tdProps }` — so moving a page across is a swap of the element, not a rewrite of its
 * columns. Sorting stays with the caller: every list here sorts on the server, and a table that
 * quietly re-sorted the page it was given would be lying about what it shows.
 */

const Scroller = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

const Grid = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${DX.fz.sm};
  line-height: ${DX.lh.sm};
  color: ${DX.text};

  /* The head is the grey strip the periods table wears: rounded, no rule under it, the captions
     doing the separating rather than a line. */
  thead {
    position: sticky;
    top: 0;
    z-index: 2;
  }

  thead th {
    background: ${DX.surfaceSunken};
  }

  thead th:first-child {
    border-top-left-radius: ${DX.radius.item};
    border-bottom-left-radius: ${DX.radius.item};
  }

  thead th:last-child {
    border-top-right-radius: ${DX.radius.item};
    border-bottom-right-radius: ${DX.radius.item};
  }

  /* Sentence case: the same decision the project list made when nine uppercase column names turned
     out to be louder than the data under them. */
  th {
    padding: ${DX.space[2]} ${DX.space[5]};
    text-align: left;
    white-space: nowrap;
    font-size: ${DX.fz.caption};
    font-weight: ${DX.fw.strong};
    line-height: ${DX.lh.caption};
    color: ${DX.textMuted};
  }

  th * {
    text-transform: none;
    letter-spacing: 0;
  }

  /* 36px rows, the same rhythm the periods table keeps. */
  td {
    height: 36px;
    padding: ${DX.space[1]} ${DX.space[5]};
    border-bottom: ${DX.hairlineSoft};
    vertical-align: middle;
  }

  tbody tr {
    transition: background-color ${DX.duration} ${DX.ease};
  }

  tbody tr[data-clickable='true'] {
    cursor: pointer;
  }

  tbody tr[data-clickable='true']:hover {
    background: ${DX.hover};
  }

  tbody tr[data-selected='true'] {
    background: ${DX.selected};
  }

  /* The row a row opens onto: it belongs to the row above it, so it carries no rule between them
     and sits on the sunken surface to say it is inside something. */
  tbody tr.dx-expansion > td {
    padding: 0;
    background: ${DX.surfaceSunken};
    border-bottom: ${DX.hairline};
  }
`;

const Empty = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: ${DX.space[7]};
  color: ${DX.textMuted};
  font-size: ${DX.fz.sm};
`;

/**
 * @param {{
 *   columns?: any[], data?: any[], isLoading?: boolean, skeletonRows?: number,
 *   onRowClick?: (item: any, index: number) => void, rowProps?: any,
 *   isSelected?: (item: any) => boolean, isExpanded?: (item: any) => boolean,
 *   renderExpanded?: (item: any) => any, getRowId?: (item: any, index: number) => any,
 *   empty?: any, idPrefix?: string
 * }} props
 */
export const DataTable = ({
  columns = [],
  data = [],
  isLoading = false,
  skeletonRows = 8,
  onRowClick,
  rowProps,
  isSelected,
  isExpanded,
  renderExpanded,
  getRowId = (item, index) => item?.id ?? index,
  empty = null,
  idPrefix = 'dx-table'
}) => {
  const showEmpty = !isLoading && data.length === 0;

  return (
    <Scroller>
      <Grid id={`${idPrefix}-container`}>
        <thead>
          <tr>
            {columns.map(column => (
              <th key={`th-${column.key}`} style={column.thStyle}>
                {column.renderHeadCell ? column.renderHeadCell() : column.title}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {isLoading &&
            Array.from({ length: skeletonRows }).map((_, rowIndex) => (
              <tr key={`skeleton-${rowIndex}`}>
                {columns.map(column => (
                  <td key={`skeleton-${rowIndex}-${column.key}`}>
                    <Skeleton height={16} />
                  </td>
                ))}
              </tr>
            ))}

          {!isLoading &&
            data.map((item, index) => {
              const id = getRowId(item, index);
              const extra = typeof rowProps === 'function' ? rowProps(item) : rowProps;
              const expanded = !!(isExpanded && renderExpanded && isExpanded(item));

              return (
                <React.Fragment key={id}>
                  <tr
                    id={`${idPrefix}-row-${id}`}
                    data-clickable={!!onRowClick}
                    data-selected={!!(isSelected && isSelected(item))}
                    onClick={onRowClick ? () => onRowClick(item, index) : undefined}
                    {...extra}
                  >
                    {columns.map(column => {
                      const tdProps =
                        typeof column.tdProps === 'function'
                          ? column.tdProps(item)
                          : column.tdProps;

                      return (
                        <td
                          key={`${id}-${column.key}`}
                          {...tdProps}
                          onClick={
                            tdProps?.onClick ? event => tdProps.onClick(item, event) : undefined
                          }
                        >
                          {column.render ? column.render(item) : item[column.key]}
                        </td>
                      );
                    })}
                  </tr>

                  {expanded && (
                    <tr className="dx-expansion">
                      <td colSpan={columns.length}>{renderExpanded(item)}</td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </tbody>
      </Grid>

      {showEmpty && (empty || <Empty>Nothing here yet.</Empty>)}
    </Scroller>
  );
};
